import dayjs from 'dayjs';
import { FORMATTED_DATES } from '../../constants/individualPortal/dashboard/dashboardModelTypes';
import { SSAP_DATE_FORMATS } from '../../constants/ssap/miscellaneous/miscellaneous';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';

export const DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_FORMAT_DASHED = 'YYYY-MM-DD';
export const DATE_FORMAT2 = 'MMM D, YYYY';
export const DATE_FORMAT3 = 'MMMM D, YYYY';
export const MMM_DD_YYY = 'MMM DD YYYY';
export const US_DATE_TIME_FORMAT_SLASH_LOWERCASE = 'MM/dd/YYYY HH:mm';
export const US_DATE_TIME_FORMAT_SLASH_UPPERCASE = 'MM/DD/YYYY HH:mm';
export const DATE_TIME_FORMAT = 'MMM D, YYYY, h:mm:ss A';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);

export const M_D_YYYY_H_MM_A = 'M/D/YYYY h:mm A';
export const MMM_DD_YYYY_HH_MM = 'MMM dd, yyyy HH:mm';
export const HH_MM = 'HH:mm';
export const MM_DD_YYYY_H_MM_SS = 'MM-DD-YYYY HH:mm:ss';

export const TIME_INTERVALS = {
    SIXTY_DAYS: 60,
    NINETY_DAYS: 90,
    ONE_YEAR: 'ONE_YEAR',
};

const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const formatDate = date => dayjs(dayjs(date), DATE_FORMAT).startOf('day');

const formatDateTime = date => dayjs(dayjs(date), DATE_FORMAT);

const getDateFormatted = (date, format = DATE_FORMAT) => dayjs(date).format(format);

const getAge = (dob, refYear) => refYear.diff(dob, 'years');
/**
 * Checks if OE is approaching: serverDate is {amount} days before OE Start Date.
 * @param serverDate
 * @param oepStartDate
 * @param amount - default 30
 * @returns {boolean}
 */
const isOEApproaching = (serverDate, oepStartDate, amount = 30) => {
    const diff = oepStartDate.diff(serverDate, 'days');
    return diff > 0 && diff <= amount;
};

/**
 * Checks if OE End Date Approaching
 * @param serverDate - timestamp
 * @param oepStartDate - timestamp
 * @param oepEndDate - timestamp
 * @returns {boolean}
 */
const isOEEndDateApproaching = (serverDate, oepStartDate, oepEndDate) => {
    return dayjs(serverDate).isBetween(oepStartDate, oepEndDate, null, true);
};

/**
 * Returns true if serverDate is between oepEndDate - 3 and oepEnrollEndDate. Inclusively.
 *
 * @returns {boolean}
 * @param serverDate
 * @param oepEndDate
 * @param oepEnrollEndDate
 */
const isExtendedOEPDates = (serverDate, oepEndDate, oepEnrollEndDate) => {
    const oepEndDateRange = oepEndDate.clone().subtract(3, 'days');
    return dayjs(serverDate).isBetween(oepEndDateRange, oepEnrollEndDate, undefined, true);
};

/**
 * Returns upcoming year for renewals.
 * @param serverDate
 * @param oepStartDate
 * @returns {number}
 */
const getUpcomingYearForRenewals = (serverDate, oepStartDate) => {
    const serverDateYear = dayjs(serverDate).year();
    const oepStartDateYear = dayjs(oepStartDate).year();

    return serverDateYear > oepStartDateYear ? serverDateYear : serverDateYear + 1;
};

const getFormattedDate = (type, date, isInsideOEWindow) => {
    switch (type) {
        case FORMATTED_DATES.serverDate:
        case FORMATTED_DATES.oepStartDate:
        case FORMATTED_DATES.exceptionalCircumstancesEndDate:
        case FORMATTED_DATES.oepEndDate:
        case FORMATTED_DATES.globalConfigCoverageStartDate:
        case SSAP_DATE_FORMATS.HRA_START_DATE:
        case FORMATTED_DATES.oepEnrollEndDate: {
            return date.format(DATE_FORMAT);
        }
        case FORMATTED_DATES.currentYear: {
            return parseInt(date);
        }
        case FORMATTED_DATES.previousYear: {
            return parseInt(date) - 1;
        }
        case FORMATTED_DATES.nextYear: {
            return isInsideOEWindow ? parseInt(date) : parseInt(date) + 1;
        }

        case FORMATTED_DATES.oeYear: {
            return dayjs(date).year();
        }

        case FORMATTED_DATES.commentMonthHour: {
            return dayjs(date).format('MMM DD   hh:mma');
        }
        case FORMATTED_DATES.commonDate: {
            return dayjs(date).utc().format(DATE_FORMAT);
        }
        case FORMATTED_DATES.exceptionalCircumstancesEndDateMMMMDo:
            return date.format('MMMM Do');
        case FORMATTED_DATES.exceptionalCircumstancesEndDateESFormat:
            return date.format('MM-DD-YYYY');
    }
};

const getYear = dateToBeProcessed => {
    let processedDate = new Date(dateToBeProcessed);
    return dayjs.utc(processedDate).year();
};

const getMonthByName = dateToBeProcessed => {
    let processedDate = new Date(dateToBeProcessed);
    return dayjs.utc(processedDate).format('MMMM');
};

const convertNumberToMonthName = m => dayjs().month(m).format('MMMM');

export const formatDateToMMDDYYYY = d => {
    if (d === null || d === '') return '';
    const date = new Date(d);
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

/**
 * Removes date info.
 * Example: "Dec 31, 2020, 12:00:00 AM" => "Dec 31, 2020"
 * @param fullDate
 * @returns {*}
 */
const removeTimeFromDateStr = fullDate => fullDate.split(',').slice(0, 2).join(', ');

const isExceptionalCircumstances = (serverDate, exceptionalCircumstancesStartDate, exceptionalCircumstancesEndDate) => {
    return dayjs(serverDate).isBetween(exceptionalCircumstancesStartDate, exceptionalCircumstancesEndDate, null, true);
};

/**
 * Format Date using IntlDate.
 * Example: "Dec 31, 2020, 12:00:00 AM" => "Dec 31, 2020"
 * @param date - String | Date
 * @param args - { options: object, locale: String }
 * @param options - object - { IntlDate options Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat }
 * @param locale - String - "en-US" | "es-US" | "fr-CA" Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
 * @returns {*}
 */
const formatDateusingIntl = (date, { options = {}, locale = 'en-US' } = {}) => {
    const formatDate = typeof date === 'string' ? new Date(date) : date;

    const dateOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...options,
    };
    return new Intl.DateTimeFormat(locale, dateOptions).format(formatDate);
};

export const formatToLocale = (date, language, format = DATE_TIME_FORMAT) =>
    dayjs(date).locale(language).format(format);

export const dateMethods = {
    convertNumberToMonthName,
    formatDate,
    formatDateTime,
    getAge,
    getFormattedDate,
    getMonthByName,
    getUpcomingYearForRenewals,
    getYear,
    isExceptionalCircumstances,
    isExtendedOEPDates,
    isOEApproaching,
    isOEEndDateApproaching,
    removeTimeFromDateStr,
    formatDateusingIntl,
    formatDateToMMDDYYYY,
    getDateFormatted,
};

export const DATE_FORMATS = {
    DATE_FORMAT,
    DATE_FORMAT2,
    DATE_TIME_FORMAT,
    US_DATE_TIME_FORMAT_SLASH_LOWERCASE,
    US_DATE_TIME_FORMAT_SLASH_UPPERCASE,
    M_D_YYYY_H_MM_A,
    MMM_DD_YYYY_HH_MM,
    HH_MM,
    MMM_DD_YYY,
    MM_DD_YYYY_H_MM_SS,
};

/**
 * Adjusts a UTC date string to the local time zone.
 * Example: "2021-08-24T14:00:00Z", -21600000, 3600000 => { sDate: 'August 24, 2021', sTime: '09:00:00 AM', sDateTime: 'August 24, 2021 at 09:00:00 AM' }
 * @param {string} utcString - The UTC date string to adjust.
 * @param {number} timezoneOffsetMs - The time zone offset in milliseconds.
 * @param {number} dstSavingsMs - The daylight savings time offset in milliseconds.
 * @param {object} options - Additional options for formatting the date. Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#locale_options
 * @returns {object} An object containing the adjusted date and time strings.
 */
export const adjustUTCToLocalTime = (utcString, timezoneOffsetMs, dstSavingsMs, options = {}) => {
    const dateOptions = {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'UTC', // Force UTC time zone for consistency,
        ...options,
    };

    // Parse the UTC time from the input string
    const utcDate = new Date(utcString);

    // Get the UTC timestamp in milliseconds
    const utcTimestamp = utcDate.getTime();

    // Calculate the adjusted timestamp
    const adjustedTimestamp = utcTimestamp + timezoneOffsetMs + dstSavingsMs;

    // Create a new Date object with the adjusted timestamp
    const adjustedDate = new Date(adjustedTimestamp);

    // Format the date part
    const sDate = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        timeZone: 'UTC', // Force UTC time zone for consistency
    }).format(adjustedDate);

    // Format the time part
    const sTime = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'UTC', // Force UTC time zone for consistency
    }).format(adjustedDate);

    const sDateTime = new Intl.DateTimeFormat('en-US', dateOptions).format(adjustedDate);

    return { sDate, sTime, sDateTime };
};

/**
 * Returns the name of the month corresponding to the given month number.
 *
 * @param {number} monthNumber - The month number (1 for January, 2 for February, etc.).
 * @returns {string} The name of the month.If the month number is not between 1 and 12.
 */
export function getMonthName(monthNumber) {
    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    if (monthNumber < 1 || monthNumber > 12) {
        ('');
    }

    return months[monthNumber - 1];
}

/**
 * Formats a given ISO date string into a specified format.
 *
 * @param {string} isoString - The ISO date string to format.
 * @param {string} format - The format string to apply.
 * @returns {string} The formatted date string.
 */
export function formatDateString(isoString, format = 'MM/DD/YYYY') {
    return dayjs(isoString).format(format);
}

/*
 * Get Number of Days between two dates.
 * Example: "2021-08-24T14:00:00Z", "2021-08-25T14:00:00Z" => 1
 * @param {string | Date} date1 - The UTC date string to adjust.
 * @param {string | Date} date2 - The time zone offset in milliseconds.
 * @returns {number} An object containing the adjusted date and time strings.
 */
export const getDifferenceInDays = (date1, date2) => {
    const firstDate = new Date(date1);
    const secondDate = new Date(date2);

    return Math.round(Math.abs((firstDate - secondDate) / ONE_DAY_IN_MILLISECONDS));
};

/**
 * Subtracts a specified number of days from a given date.
 */
export const substractDays = (date, days) => dayjs(date).subtract(days, 'days').toDate();

/**
 * Subtracts a specified number of months from a given date.
 */
export const substractMonths = (date, months) => dayjs(date).subtract(months, 'months').toDate();

/**
 * Gets a current date
 */
export const getCurrentDate = () => dayjs().toDate();

/**
 * Gets a start of the month for a given date.
 */
export const getStartOfMonth = date => dayjs(date).startOf('month').toDate();

/**
 * Gets an end of the month for a given date.
 */
export const getEndOfMonth = date => dayjs(date).endOf('month').toDate();

/**
 * Gets a start of the day for a given date.
 */
export const getStartOfDay = date => dayjs(date).startOf('day').toDate();

/**
 * Gets an end of the day for a given date.
 */
export const getEndOfDay = date => dayjs(date).endOf('day').toDate();

/**
 *
 * @param value the value you want to check is a date
 * @returns true if value if date else false
 */
export function isValidDate(value) {
    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime());
}
